import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable ,  BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorHandlerService, HandleError } from './http-error-handling.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type':  'application/json',
    'Authorization': 'my-auth-token'
  }),
};

@Injectable()
export class ProductService {

  private productDataSubject = new BehaviorSubject<ProductOutput>(null);
  public productData = this.productDataSubject.asObservable();

  private productParametersSubject = new BehaviorSubject<ProductInput>(null);
  public productParameters = this.productParametersSubject.asObservable();

  private productValidatorSubject = new BehaviorSubject<String>(null);
  public productValidator = this.productValidatorSubject.asObservable();

  private handleError: HandleError;

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService) {
    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    this.handleError = httpErrorHandler.createHandleError('ProductService');
  }

  public getMotorVehicleVariationsList(type, oid, purpose, securityType, year, collateralClass?): Observable<any> {
    const usage = purpose.indexOf('personal') !== -1 ? 'personal' : (purpose.indexOf('private') !== -1) ? 'personal' : 'business';
    return this.http
      .get<any[]>(`${environment.api.product}/variation/${type}/${oid}/products?usage=${usage}&securityType=${securityType}&year=${year}&class=${collateralClass}`, httpOptions)
      .pipe(
        catchError(this.handleError('getMotorVehicleVariationsList', []))
      );
  }

  public getHomeLoanVariationsList(params, imported = false): Observable<any> {
    const { type, oid } = params;
    delete params.type;
    delete params.oid;


    Object.keys(params).forEach(k => {
      if (params[k] === null || params[k] === undefined) {
        delete params[k];
      }
    });

    // Append imported as a query param if imported === true
    if (imported === true) {
      params.imported = imported;
    } else {
      delete params.imported;
    }

    const httpOpts = {
      params,
      headers: httpOptions.headers
    };

    return this.http
      .get<any[]>(`${environment.api.product}/products/${type}/${oid}`, httpOpts)
      .pipe(
        map((res: VxProduct[]) => {
          this.productDataSubject.next({ input: params, output: res });
          return res;
        }),
        catchError(this.handleError('getHomeLoanVariationsList', []))
      );
  }

  public getVariationDetails(type, oid, id, securityType, year) {
    return this.http
      .get<any[]>(`${environment.api.product}/variation/${type}/${oid}/product/${id}?securityType=${securityType}&year=${year}`, httpOptions)
      .pipe(
        catchError(this.handleError('getVariationDetails', []))
      );
  }

  public getBalloons(type, purpose, year, term, collateralClass = 'car') {
    return this.http
      .get<any[]>(`${environment.api.product}/${type}/${purpose}/${year}/${term}/balloons/${collateralClass}`, httpOptions)
      .pipe(
        catchError(this.handleError('getBalloons', []))
      );
  }

  public setProductParameters(params: ProductInput) {
    this.productParametersSubject.next(params);
  }

  getProductParameters(): Observable<any> {
    return this.productParametersSubject.asObservable();
  }

  public setProductData(params: ProductOutput) {
    this.productDataSubject.next(params);
  }
}


// used for home loan updates
export interface ProductInput {
  oid?: string;
  type?: string;
  occupancyType?: string;
  interestType?: string;
  repaymentType?: string;
  loanAmount?: string;
  primaryPurpose?: string;
  purposeGroupType?: string;
  lvr?: string;
  isSMSF?: boolean;
}

export interface VxProduct {
  pricing: any;
  displayName: any;
  terms: any;
  slug: any;
}
export interface ProductOutput {
  input?: ProductInput;
  output?: VxProduct[];
}
